import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { PreviewCarousel } from './preview-carousel';
import { Header } from './carousel-decorators/header';
import { ValidationMessage } from './carousel-decorators/validation-message';
import { DisclaimerMessage } from './carousel-decorators/disclaimer-message';
import { FooterButton } from './carousel-decorators/footer-button';
import { AllPlacements } from './carousel-decorators/all-placements';

import { getPreviewCarouselLayoutStyles } from './styles';

export const PreviewCarouselLayout = (props) => {
  const {
    previewElements,
    getImageInfoByIndex,
    validateImageSizeByIndex,
    enableAutoPlay,
    styleOverrides,
    hideHeaderButtons,
    hideNavigationControlsIfSingle,
    doesTemplateShowImageByIndex,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getPreviewCarouselLayoutStyles({ styleOverrides }));
  const [index, setIndex] = useState(0);
  const isImageShown = useMemo(() => doesTemplateShowImageByIndex(index), [doesTemplateShowImageByIndex, index]);

  const getSelectedImage = useCallback((imageIndex) => {
    const isImageBasedPlacement = doesTemplateShowImageByIndex(imageIndex);

    if (isImageBasedPlacement) {
      return getImageInfoByIndex(imageIndex);
    }

    return {
      isIncluded: true,
      isRecommended: false,
    };
  }, [doesTemplateShowImageByIndex, getImageInfoByIndex]);

  const selectedImage = useMemo(() => {
    if (isImageShown) {
      return getImageInfoByIndex(index);
    }
    return {
      isIncluded: true,
      isRecommended: false,
    };
  }, [getImageInfoByIndex, index, isImageShown]);

  const isImageValid = useMemo(() => {
    if (!isImageShown) {
      return true;
    }
    return validateImageSizeByIndex(index);
  }, [validateImageSizeByIndex, index, isImageShown]);

  return (
    <div className={classnames('new-preview-carousel-container-adprelib', classes.root)}>
      <Header
        index={index}
        selectedImage={selectedImage}
        hideHeaderButtons={hideHeaderButtons}
        isImageValid={isImageValid}
        {...props}
      />
      <ValidationMessage
        index={index}
        selectedImage={selectedImage}
        isImageValid={isImageValid}
        {...props}
      />
      <PreviewCarousel
        i18n={i18n}
        previewElements={previewElements}
        index={index}
        setIndex={setIndex}
        enableAutoPlay={enableAutoPlay}
        hideNavigationControlsIfSingle={hideNavigationControlsIfSingle}
        styleOverrides={styleOverrides}
      />
      <DisclaimerMessage
        {...props}
      />
      <FooterButton
        index={index}
        selectedImage={selectedImage}
        {...props}
      />
      <AllPlacements
        index={index}
        getSelectedImage={getSelectedImage}
        isImageValid={isImageValid}
        {...props}
      />
    </div>
  );
};

PreviewCarouselLayout.propTypes = {
  previewElements: PropTypes.arrayOf(PropTypes.func).isRequired,
  hideNavigationControlsIfSingle: PropTypes.bool,
  // helper methods
  getImageInfoByIndex: PropTypes.func.isRequired,
  getCarouselTitleByIndex: PropTypes.func.isRequired,
  getMinSizeByIndex: PropTypes.func.isRequired,
  validateImageSizeByIndex: PropTypes.func.isRequired,
  doesTemplateShowImageByIndex: PropTypes.func.isRequired,
  hideHeaderButtons: PropTypes.bool,

  // header decorator props
  headerButtonProps: PropTypes.shape({}),

  // disclaimer message props
  disclaimerMessage: PropTypes.string,

  // footer button props
  footerButtonProps: PropTypes.shape({}),

  // custom style overrides
  styleOverrides: PropTypes.shape({}),

  // enable behavior in carousel to auto play through carousel items
  enableAutoPlay: PropTypes.bool,
};

PreviewCarouselLayout.defaultProps = {
  hideNavigationControlsIfSingle: false,
  headerButtonProps: null,
  disclaimerMessage: '',
  footerButtonProps: null,
  styleOverrides: {},
  enableAutoPlay: true,
  hideHeaderButtons: false,
};
